// src/components/Stripe/PaymentPage.js
import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Divider,
  Fade,
  Chip,
  useTheme,
  alpha,
  Stack,
} from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import LockIcon from '@mui/icons-material/Lock';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { AuthContext } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import API from '../config';
import CheckoutForm from './CheckoutForm';

// Initialize Stripe with publishable key from environment variable
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 
  'pk_test_your_stripe_publishable_key' // Fallback for development
);

export default function PaymentPage() {
  const { authData } = useContext(AuthContext);
  const token = authData?.access_token;
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  
  const [clientSecret, setClientSecret] = useState('');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Get the payment intent client secret from the URL query parameters or from the API
  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }

    // Check if we have a payment intent from query params
    const queryParams = new URLSearchParams(location.search);
    const paymentIntentId = queryParams.get('payment_intent');
    const priceId = queryParams.get('price_id');
    
    if (paymentIntentId) {
      // If we have a payment intent ID, retrieve the client secret
      axios.get(`${API}/api/payment/intent/${paymentIntentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(response => {
          setClientSecret(response.data.clientSecret);
          setAmount(response.data.amount);
          setLoading(false);
        })
        .catch(err => {
          console.error('Error retrieving payment intent:', err);
          setError('Could not retrieve payment information. Please try again.');
          setLoading(false);
        });
    } else if (priceId) {
      // If we have a price ID, create a new payment intent
      axios.post(`${API}/api/payment/create-intent`, 
        { priceId },
        { headers: { Authorization: `Bearer ${token}` } }
      )
        .then(response => {
          setClientSecret(response.data.clientSecret);
          setAmount(response.data.amount);
          setLoading(false);
        })
        .catch(err => {
          console.error('Error creating payment intent:', err);
          setError('Could not set up payment. Please try again.');
          setLoading(false);
        });
    } else {
      setError('Missing payment information. Please try again.');
      setLoading(false);
    }
  }, [token, navigate, location.search]);

  const handlePaymentSuccess = (paymentIntent) => {
    // Navigate to success page or show success message
    navigate('/success', { 
      state: { 
        paymentId: paymentIntent.id,
        amount: amount
      } 
    });
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '60vh' 
      }}>
        <CircularProgress size={48} thickness={4} />
        <Typography variant="h6" sx={{ mt: 3, fontWeight: 500 }}>
          Setting up your payment...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Alert 
          severity="error" 
          variant="filled"
          sx={{
            borderRadius: 2,
            boxShadow: 3,
            mb: 3
          }}
        >
          {error}
        </Alert>
        <Box sx={{ textAlign: 'center' }}>
          <Button 
            variant="outlined" 
            onClick={() => navigate(-1)}
            startIcon={<ArrowBackIcon />}
            sx={{ 
              borderRadius: 2,
              py: 1,
              px: 3
            }}
          >
            Go Back
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 4, md: 8 } }}>
      <Fade in={true} timeout={600}>
        <Paper 
          elevation={4} 
          sx={{ 
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          }}
        >
          {/* Header */}
          <Box 
            sx={{ 
              p: 4, 
              pb: 3,
              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: 'radial-gradient(circle at top right, rgba(255,255,255,0.15) 0%, transparent 60%)',
                zIndex: 0,
              },
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: 'rgba(255,255,255,0.2)',
                    mr: 2,
                  }}
                >
                  <PaymentIcon />
                </Box>
                <Typography variant="h5" component="h1" fontWeight={700}>
                  Complete Your Payment
                </Typography>
              </Box>
              
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Please enter your payment details to complete your subscription
              </Typography>
              
              {amount && (
                <Chip 
                  label={`Amount: ${amount}`} 
                  color="secondary" 
                  sx={{ 
                    mt: 2,
                    fontWeight: 600,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    '& .MuiChip-label': {
                      px: 2,
                    }
                  }} 
                />
              )}
            </Box>
          </Box>
          
          <Divider />
          
          {/* Payment Form */}
          <Box sx={{ p: 4 }}>
            {clientSecret && (
              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <CheckoutForm 
                  clientSecret={clientSecret} 
                  amount={amount} 
                  onSuccess={handlePaymentSuccess} 
                />
              </Elements>
            )}
            
            {/* Security notice */}
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mt: 4,
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.success.main, 0.1),
              }}
            >
              <LockIcon sx={{ color: theme.palette.success.main, mr: 1.5 }} />
              <Typography variant="body2" color="text.secondary">
                Your payment information is encrypted and secure. We use industry-standard security measures to protect your data.
              </Typography>
            </Box>
          </Box>
          
          {/* Footer */}
          <Box 
            sx={{ 
              p: 3, 
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Button 
              variant="outlined" 
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIcon />}
              sx={{ 
                borderRadius: 2,
              }}
            >
              Go Back
            </Button>
            
            <Stack direction="row" spacing={1} alignItems="center">
              <Box 
                component="img" 
                src="https://cdn.jsdelivr.net/gh/stripe/press-kit@master/logos/word.svg" 
                alt="Powered by Stripe" 
                sx={{ height: 20 }} 
              />
            </Stack>
          </Box>
        </Paper>
      </Fade>
    </Container>
  );
}
